import {t} from 'i18next';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {COMMENT_CHAR_LIMIT, COMMENT_REGEX} from '../../../../shared/constants';
import {isEmpty} from '../../../../utils/formatters';
import ArrModal, {ModalButton} from '../../../common/arr-modal/ArrModal';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import './AddCommentDialog.scss';

export interface AddCommentDialogProps {
  showModal: boolean;
  requestLoading?: boolean;
  customError?: string;
  onSave?: (commentText: string) => void;
  onClose?: () => void;
}

const AddCommentDialog: React.FC<AddCommentDialogProps> = ({
  showModal,
  requestLoading,
  customError = '',
  onSave,
  onClose,
}: AddCommentDialogProps) => {
  const [comment, setComment] = useState<string>('');
  const [commentValidationError, setCommentValidationError] =
    useState<string>(customError);
  const [announcementValue, setAnnoucementValue] = useState<string>('');

  useEffect(() => {
    if (showModal) {
      setCommentValidationError('');
      setComment('');
    }
  }, [showModal]);

  useEffect(() => {
    if (!isEmpty(customError)) {
      setCommentValidationError(customError);
    }
  }, [customError]);

  const handleCommentChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      let commentError = '';
      if (isEmpty(value)) {
        commentError = t(
          'manageCommentFields.addComment.errors.commentMandatory'
        );
      } else if (value.length > COMMENT_CHAR_LIMIT) {
        commentError = t(
          'manageCommentFields.addComment.errors.commentExceedCharacters'
        );
      } else if (!COMMENT_REGEX.test(value)) {
        commentError = t(
          'manageCommentFields.addComment.errors.commentOnlyAlphaNumeric'
        );
      }

      setCommentValidationError(commentError);
      setComment(value);
    },
    []
  );

  useEffect(() => {
    if (commentValidationError) {
      setAnnoucementValue('');
      return;
    }
    const timeout = setTimeout(
      () =>
        setAnnoucementValue(
          comment?.trim()
            ? t('manageCommentFields.addComment.saveButtonReaderValue')
            : ''
        ),
      200
    );

    return () => clearTimeout(timeout);
  }, [comment]);

  const isSaveButtonDisabled: boolean =
    requestLoading || !isEmpty(commentValidationError) || isEmpty(comment);

  const dialogActionButtons: ModalButton[] = useMemo(
    () => [
      {
        id: 'close-action',
        label: t('common.cancel'),
        onClick: () => onClose?.(),
        variant: 'secondary',
        disabled: requestLoading,
      },
      {
        id: 'save-action',
        label: t('common.save'),
        onClick: () => onSave?.(comment),
        variant: 'primary',
        loading: requestLoading,
        disabled: isSaveButtonDisabled,
        ariaDisabled: isSaveButtonDisabled ? 'true' : 'false',
      },
    ],
    [comment, commentValidationError, onClose, onSave, requestLoading]
  );

  const isErrorEmpty = useMemo(
    () => isEmpty(commentValidationError),
    [commentValidationError]
  );

  return (
    <ArrModal
      showClose
      showModal={showModal}
      disabledClose={requestLoading}
      onClose={() => onClose?.()}
      autoFocusCloseButton={true}
      title={t('manageCommentFields.addComment.addCommentField')}
      buttons={dialogActionButtons}
      className="add-comment-dialog-box"
      dataTestId="add-comment-dialog-box"
    >
      <ArrTextField
        dataTestId={'add-comment-field'}
        state={isErrorEmpty ? 'enabled' : 'error'}
        label={t('manageCommentFields.addComment.commentField')}
        placeholder={t('manageCommentFields.addComment.addCommentField')}
        disabled={requestLoading}
        autoFocus
        slotProps={{
          input: {
            readOnly: requestLoading,
          },
        }}
        supportText={commentValidationError}
        showErrorIcon={!isErrorEmpty}
        aria-invalid={!isErrorEmpty}
        onChange={handleCommentChange}
        name={'comment-field'}
      />
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        data-testid={`add-comment-dialog-announcement-value`}
      >
        {announcementValue}
      </div>
    </ArrModal>
  );
};

export {AddCommentDialog};
